import { __awaiter } from "tslib";
import { EventCenter } from './../../canvas/event-center';
import { isEqual, pick } from 'lodash';
import { BaseNode } from '../../canvas/element/node';
import { Command } from '../../command';
import { getElementCategory } from '../common';
import { CommandHistoryModel } from './command-history';
export class CanvasModel {
    constructor({ canvas, canvasId, oFlowModel, canvasData: initCanvasData = {}, rankConfig = {}, }) {
        this.selectedElementsId = [];
        this.hoveredElementId = null;
        this.canvasElementInstanceMap = {};
        this.bindCanvasElement = (id, element) => {
            this.canvasElementInstanceMap[id] = element;
        };
        this.unBindCanvasElement = (id) => {
            delete this.canvasElementInstanceMap[id];
        };
        this.getCanvasData = () => {
            return Object.values(this.canvasElementInstanceMap).reduce((init, elementInstance) => {
                const category = getElementCategory(elementInstance);
                init[`${category.toLowerCase()}s`].push(elementInstance.getConfig());
                return init;
            }, {
                nodes: [],
                edges: [],
                groups: [],
            });
        };
        this.findNodeInstance = (params = {}) => {
            const { nodes } = this.getCanvasData();
            const targetConfig = nodes === null || nodes === void 0 ? void 0 : nodes.find(nodeConfig => {
                if (typeof params === 'object') {
                    let isMatch = true;
                    for (let key of Object.keys(params)) {
                        if (!isEqual(params[key], nodeConfig[key])) {
                            isMatch = false;
                            break;
                        }
                    }
                    return isMatch;
                }
                else {
                    return params(nodeConfig);
                }
            });
            return targetConfig ? this.getCanvasElementInstance(targetConfig === null || targetConfig === void 0 ? void 0 : targetConfig.id) : null;
        };
        this.batchFindEdgeInstance = (params = {}) => {
            const { edges } = this.getCanvasData();
            const edgesInstances = [];
            edges === null || edges === void 0 ? void 0 : edges.forEach(edgeConfig => {
                let isMatch = true;
                for (let key of Object.keys(params)) {
                    if (!isEqual(params[key], edgeConfig[key])) {
                        isMatch = false;
                        break;
                    }
                }
                if (isMatch) {
                    const edgeInstance = this.getCanvasElementInstance(edgeConfig.id);
                    edgeInstance && edgesInstances.push(edgeInstance);
                }
            });
            return edgesInstances;
        };
        this.batchFindNodeInstance = (params = {}) => {
            const { nodes } = this.getCanvasData();
            const nodesInstances = [];
            nodes === null || nodes === void 0 ? void 0 : nodes.forEach(nodeConfig => {
                let isMatch = true;
                for (let key of Object.keys(params)) {
                    if (!isEqual(params[key], nodeConfig[key])) {
                        isMatch = false;
                        break;
                    }
                }
                if (isMatch) {
                    const edgeInstance = this.getCanvasElementInstance(nodeConfig.id);
                    edgeInstance && nodesInstances.push(edgeInstance);
                }
            });
            return nodesInstances;
        };
        this.findEdgeInstance = (params = {}) => {
            const { edges } = this.getCanvasData();
            const targetConfig = edges === null || edges === void 0 ? void 0 : edges.find(edgeConfig => {
                let isMatch = true;
                for (let key of Object.keys(params)) {
                    if (!isEqual(params[key], edgeConfig[key])) {
                        isMatch = false;
                        break;
                    }
                }
                return isMatch;
            });
            return targetConfig ? this.getCanvasElementInstance(targetConfig === null || targetConfig === void 0 ? void 0 : targetConfig.id) : null;
        };
        this.getAllNextElemens = (node) => {
            const nodeInstance = node instanceof BaseNode ? node : this.getCanvasElementInstance(node);
            const getAll = (ins, nodeIds = []) => {
                const next = ins.getNextElements();
                next.nodes.forEach(item => {
                    nodeIds.push(item.id);
                    getAll(item, nodeIds);
                });
                return nodeIds;
            };
            return Array.from(new Set(getAll(nodeInstance))).map(id => this.canvasElementInstanceMap[id]);
        };
        this.getAllPreviousElements = (node) => {
            const nodeInstance = node instanceof BaseNode ? node : this.getCanvasElementInstance(node);
            const getAll = (ins, nodeIds = []) => {
                const previous = ins.getPreviousElements();
                previous.nodes.forEach(item => {
                    nodeIds.push(item.id);
                    getAll(item, nodeIds);
                });
                return nodeIds;
            };
            return Array.from(new Set(getAll(nodeInstance))).map(id => this.canvasElementInstanceMap[id]);
        };
        this.generatePasteElementConfigByIds = (payload) => {
            var _a;
            return (_a = payload
                .map(id => this.canvasElementInstanceMap[id])) === null || _a === void 0 ? void 0 : _a.filter(element => {
                return element.couldCopy;
            }).reduce((init, element) => {
                const category = getElementCategory(element);
                if (category === 'NODE') {
                    init.nodes.push(element.getConfig());
                }
                else if (category === 'EDGE') {
                    const { sourceNodeInstance, targetNodeInstance } = element;
                    const isSourceInPayload = payload === null || payload === void 0 ? void 0 : payload.includes(sourceNodeInstance.id);
                    const isTargetInPayload = payload === null || payload === void 0 ? void 0 : payload.includes(targetNodeInstance.id);
                    if (isSourceInPayload && isTargetInPayload) {
                        init.edges.push(element.getConfig());
                    }
                }
                else if (category === 'GROUP') {
                    // todo
                    init.groups.push(element === null || element === void 0 ? void 0 : element.getConfig());
                }
                return init;
            }, {
                nodes: [],
                edges: [],
                groups: [],
            });
        };
        this.undoCanvasData = () => {
            const previousCanvasData = this.commandHistoryModel.dispatchUndoCanvasData();
            previousCanvasData && this.setCanvasData(previousCanvasData);
            const { couldRedoCommand, couldUndoCommand } = this.commandHistoryModel;
            this.eventCenter.trigger('COMMAND_HISTORY_CHANGE', { couldRedoCommand, couldUndoCommand });
        };
        this.redoCanvasData = () => {
            const lastCanvasData = this.commandHistoryModel.dispatchRedoCanvasData();
            lastCanvasData && this.setCanvasData(lastCanvasData);
            const { couldRedoCommand, couldUndoCommand } = this.commandHistoryModel;
            this.eventCenter.trigger('COMMAND_HISTORY_CHANGE', { couldRedoCommand, couldUndoCommand });
        };
        this.traceCanvasData = () => {
            const canvasData = this.getCanvasData();
            this.commandHistoryModel.pushHistoryRecord(canvasData);
            const { couldRedoCommand, couldUndoCommand } = this.commandHistoryModel;
            this.eventCenter.trigger('COMMAND_HISTORY_CHANGE', { couldRedoCommand, couldUndoCommand });
        };
        this.resetCommandhistory = () => {
            this.commandHistoryModel.resetCommandHistory();
            const { couldRedoCommand, couldUndoCommand } = this.commandHistoryModel;
            this.eventCenter.trigger('COMMAND_HISTORY_CHANGE', { couldRedoCommand, couldUndoCommand });
        };
        this.setHoveredElementId = (id = null) => {
            var _a, _b;
            const previousHoveredElement = this.canvasElementInstanceMap[this.hoveredElementId];
            const hoveredElement = this.canvasElementInstanceMap[id];
            const { selectedElementsId = [] } = this;
            const previoushighlightElements = Array.from(new Set((_a = selectedElementsId === null || selectedElementsId === void 0 ? void 0 : selectedElementsId.map(id => this.canvasElementInstanceMap[id]).concat(previousHoveredElement)) === null || _a === void 0 ? void 0 : _a.filter(Boolean)));
            const highlightElements = Array.from(new Set((_b = selectedElementsId === null || selectedElementsId === void 0 ? void 0 : selectedElementsId.map(id => this.canvasElementInstanceMap[id]).concat(hoveredElement)) === null || _b === void 0 ? void 0 : _b.filter(Boolean)));
            this.hoveredElementId = id;
            previoushighlightElements === null || previoushighlightElements === void 0 ? void 0 : previoushighlightElements.forEach(e => {
                if (!(highlightElements === null || highlightElements === void 0 ? void 0 : highlightElements.find(item => item === e))) {
                    e.sethighlight(false);
                }
            });
            highlightElements === null || highlightElements === void 0 ? void 0 : highlightElements.forEach(e => {
                e.sethighlight(true);
            });
            this.eventCenter.trigger('ELEMENT_HIGHLIGHT', {
                previoushighlightElements,
                highlightElements,
            });
        };
        this.getSelectedElements = () => {
            const { selectedElementsId = [] } = this;
            return selectedElementsId === null || selectedElementsId === void 0 ? void 0 : selectedElementsId.map(id => this.canvasElementInstanceMap[id]);
        };
        this.setSelectedElementIds = (params = []) => {
            var _a, _b;
            const filterParams = params === null || params === void 0 ? void 0 : params.filter(id => this.canvasElementInstanceMap[id]);
            const { hoveredElementId } = this;
            const priviousSelectedElements = this.getSelectedElements();
            this.selectedElementsId = filterParams;
            const selectedElements = this.getSelectedElements();
            const hoveredElement = this.canvasElementInstanceMap[hoveredElementId];
            const previoushighlightElements = Array.from(new Set((_a = priviousSelectedElements.concat(hoveredElement)) === null || _a === void 0 ? void 0 : _a.filter(Boolean)));
            const highlightElements = Array.from(new Set((_b = selectedElements === null || selectedElements === void 0 ? void 0 : selectedElements.concat(hoveredElement)) === null || _b === void 0 ? void 0 : _b.filter(Boolean)));
            previoushighlightElements === null || previoushighlightElements === void 0 ? void 0 : previoushighlightElements.forEach(e => {
                if (!(highlightElements === null || highlightElements === void 0 ? void 0 : highlightElements.find(item => item === e))) {
                    e.sethighlight(false);
                }
            });
            highlightElements === null || highlightElements === void 0 ? void 0 : highlightElements.forEach(e => {
                e.sethighlight(true);
            });
            this.eventCenter.trigger('ELEMENT_HIGHLIGHT', {
                previoushighlightElements,
                highlightElements,
            });
            this.eventCenter.trigger('ELEMENT_SELECT', {
                priviousSelectedElements,
                selectedElements,
            });
        };
        // todo，大量的diff，需要考虑性能问题。
        this.setCanvasData = (fn, params = {}) => __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
            const { traceCommand, changeDataOnly } = params;
            const canvasData = this.getCanvasData();
            let res;
            if (typeof fn === 'function') {
                res = yield fn(canvasData);
            }
            else {
                res = fn;
            }
            const newCanvasIdConfigMap = (({ nodes, groups, edges }) => {
                const configIdMap = {};
                nodes === null || nodes === void 0 ? void 0 : nodes.forEach(node => {
                    configIdMap[node.id] = node;
                });
                groups === null || groups === void 0 ? void 0 : groups.forEach(group => {
                    configIdMap[group.id] = group;
                });
                edges === null || edges === void 0 ? void 0 : edges.forEach(edge => {
                    configIdMap[edge.id] = edge;
                });
                return {
                    values: configIdMap,
                    deleteConfigById: id => {
                        delete configIdMap[id];
                    },
                    getConfigById: id => {
                        return configIdMap[id];
                    },
                };
            })(res);
            // 删除多余的
            const { deletedEdgeIds, deletedNodeIds, deletedGroupIds } = Object.entries(this.canvasElementInstanceMap).reduce((init, cur) => {
                const [id, elementInstance] = cur;
                const newConfig = newCanvasIdConfigMap.getConfigById(id);
                const previousConfig = elementInstance === null || elementInstance === void 0 ? void 0 : elementInstance.getConfig();
                const category = getElementCategory(elementInstance);
                const isConfigChanged = !isEqual(newConfig, previousConfig);
                if (!newConfig) {
                    if (category === 'EDGE') {
                        init.deletedEdgeIds.push(id);
                    }
                    else if (category === 'NODE') {
                        init.deletedNodeIds.push(id);
                    }
                    else {
                        init.deletedGroupIds.push(id);
                    }
                }
                else if (isConfigChanged) {
                    if (category === 'NODE') {
                        ;
                        elementInstance.setConfig(newConfig);
                        newCanvasIdConfigMap.deleteConfigById(id);
                    }
                    else if (category === 'EDGE') {
                        const isConnectionChange = !isEqual(pick(newConfig, ['target', 'source', 'targetHandle', 'sourceHandle', 'type']), pick(previousConfig, ['target', 'source', 'targetHandle', 'sourceHandle', 'type']));
                        if (isConnectionChange) {
                            init.deletedEdgeIds.push(id);
                        }
                        else {
                            ;
                            elementInstance.setConfig(newConfig);
                            newCanvasIdConfigMap.deleteConfigById(id);
                        }
                    }
                    else if (category === 'GROUP') {
                    }
                }
                else {
                    newCanvasIdConfigMap.deleteConfigById(id);
                }
                return init;
            }, {
                movedGroups: [],
                movedNodes: [],
                deletedEdgeIds: [],
                deletedNodeIds: [],
                deletedGroupIds: [],
            });
            const { addedNodes, addedEdges, addedGroups } = Object.entries(newCanvasIdConfigMap.values).reduce((init, cur) => {
                const [, newConfig] = cur;
                const category = getElementCategory(newConfig);
                if (category === 'NODE') {
                    init.addedNodes.push(newConfig);
                }
                else if (category === 'EDGE') {
                    init.addedEdges.push(newConfig);
                }
                else if (category === 'GROUP') {
                    init.addedGroups.push(newConfig);
                }
                return init;
            }, {
                addedGroups: [],
                addedEdges: [],
                addedNodes: [],
            });
            if (!changeDataOnly) {
                (deletedNodeIds === null || deletedNodeIds === void 0 ? void 0 : deletedNodeIds.length) && (yield ((_b = (_a = this.canvas) === null || _a === void 0 ? void 0 : _a.batchDeleteNodesById) === null || _b === void 0 ? void 0 : _b.call(_a, deletedNodeIds)));
            }
            deletedNodeIds.forEach(this.unBindCanvasElement);
            if (!changeDataOnly) {
                yield ((_d = (_c = this.canvas) === null || _c === void 0 ? void 0 : _c.batchDeleteGroupsById) === null || _d === void 0 ? void 0 : _d.call(_c, deletedGroupIds));
            }
            deletedGroupIds.forEach(this.unBindCanvasElement);
            if (!changeDataOnly) {
                yield ((_f = (_e = this.canvas) === null || _e === void 0 ? void 0 : _e.batchDeleteEdgesById) === null || _f === void 0 ? void 0 : _f.call(_e, deletedEdgeIds));
            }
            deletedEdgeIds.forEach(this.unBindCanvasElement);
            if (!changeDataOnly) {
                (addedNodes === null || addedNodes === void 0 ? void 0 : addedNodes.length) && (yield ((_h = (_g = this.canvas) === null || _g === void 0 ? void 0 : _g.batchAddNodesByConfig) === null || _h === void 0 ? void 0 : _h.call(_g, addedNodes)));
                (addedGroups === null || addedGroups === void 0 ? void 0 : addedGroups.length) && (yield ((_k = (_j = this.canvas) === null || _j === void 0 ? void 0 : _j.batchAddGroupByConfig) === null || _k === void 0 ? void 0 : _k.call(_j, addedGroups)));
                (addedEdges === null || addedEdges === void 0 ? void 0 : addedEdges.length) && (yield ((_m = (_l = this.canvas) === null || _l === void 0 ? void 0 : _l.batchAddEdgesByConfig) === null || _m === void 0 ? void 0 : _m.call(_l, addedEdges)));
            }
            // 去掉删除的选择
            const newSelectedElementsId = this.selectedElementsId.filter(id => this.canvasElementInstanceMap[id]);
            if (newSelectedElementsId.length < this.selectedElementsId.length) {
                this.setSelectedElementIds(newSelectedElementsId);
            }
            // 记录历史，待优化
            if (traceCommand) {
                setTimeout(() => {
                    this.traceCanvasData();
                }, 50);
            }
        });
        this.rankConfigListenerList = [];
        // 排列顺序
        this.setRankConfig = (config) => {
            const newConfig = Object.assign({}, this.rankConfig, config);
            this.rankConfig = newConfig;
            this.rankConfigListenerList.forEach(fn => {
                fn === null || fn === void 0 ? void 0 : fn(newConfig);
            });
        };
        this.onRankConfigChange = fn => {
            this.rankConfigListenerList.push(fn);
        };
        this.remvoeRankConfigChange = fn => {
            const idx = this.rankConfigListenerList.findIndex(i => i === fn);
            this.rankConfigListenerList.splice(idx, 1);
        };
        this.canvasId = canvasId;
        this.oFlowModel = oFlowModel;
        this.rankConfig = Object.assign({ direction: 'vertical', 
            // 层级间距
            levelSpacing: 60, 
            // 节点间距
            nodesep: 100, 
            // 禁止切换方向
            disableChangeDirection: false }, rankConfig);
        this.command = new Command({ canvasId, canvas, model: this });
        this.canvas = canvas;
        this.commandHistoryModel = new CommandHistoryModel({
            canvasId,
            initCanvasData,
            canvasModel: this,
        });
        this.eventCenter = new EventCenter({ canvasModel: this });
    }
    getCanvasElementInstance(id) {
        return this.canvasElementInstanceMap[id];
    }
}
//# sourceMappingURL=index.js.map